/**
 * Import daily price history from a CSV file.
 *
 * Free price APIs sometimes refuse automated requests, but downloading a CSV
 * in a browser still works. This panel accepts those exports (Yahoo Finance,
 * Stooq, Nasdaq, most brokers) so prices, valuation ratios and forecasts can
 * be populated without depending on a live API.
 */

import React, { useRef, useState } from "react";
import { apiClient } from "@/api/client";
import type { IngestionReport } from "@/types";

export default function ImportPricesPanel({
  onImported,
}: {
  onImported?: (report: IngestionReport) => void;
}): React.ReactElement {
  const [symbol, setSymbol] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const submit = async (): Promise<void> => {
    const ticker = symbol.trim().toUpperCase();
    if (!ticker || !file) {
      setError("Choose a CSV file and enter the ticker it belongs to.");
      return;
    }
    setIsUploading(true);
    setError(null);
    setMessage(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await apiClient.post<IngestionReport>(
        `/providers/import-prices/${ticker}`,
        form,
      );
      const prices = data.prices;
      if (prices && prices.available !== false) {
        setMessage(`${ticker}: ${prices.note ?? `${prices.records} bars imported.`}`);
      } else {
        setError(prices?.note ?? "No usable rows were found in that file.");
      }
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      onImported?.(data);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ? String(detail) : "Couldn't import that file.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="panel p-5">
      <h2 className="text-sm font-semibold text-mist-100">Import prices from a CSV</h2>
      <p className="mt-1 text-sm text-mist-400">
        No signup needed. Type a ticker, click a download link (it saves a <code>.csv</code> to your
        Downloads folder), then choose that file below and import.
      </p>

      {symbol.trim() && (
        <p className="mt-2 text-xs text-mist-400">
          Download {symbol.trim().toUpperCase()} history:{" "}
          <a
            href={`https://stooq.com/q/d/l/?s=${symbol.trim().toLowerCase()}.us&i=d`}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-signal-teal hover:underline"
          >
            Stooq CSV
          </a>
          {" · "}
          <a
            href={`https://www.nasdaq.com/market-activity/stocks/${symbol
              .trim()
              .toLowerCase()}/historical`}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-signal-teal hover:underline"
          >
            Nasdaq
          </a>
          {" · "}
          <a
            href={`https://finance.yahoo.com/quote/${symbol.trim().toUpperCase()}/history`}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-signal-teal hover:underline"
          >
            Yahoo
          </a>
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={symbol}
          onChange={(event) => setSymbol(event.target.value)}
          placeholder="Ticker (e.g. AAPL)"
          className="w-40 rounded-lg border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-mist-100 placeholder:text-mist-500 focus:border-ink-500 focus:outline-none"
        />
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv,text/plain"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          className="max-w-[16rem] text-sm text-mist-300 file:mr-3 file:rounded-lg file:border-0 file:bg-ink-700 file:px-3 file:py-2 file:text-sm file:text-mist-100"
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={isUploading}
          className="rounded-lg border border-ink-600 bg-ink-800 px-4 py-2 text-sm font-medium text-mist-100 transition hover:border-ink-500 disabled:opacity-60"
        >
          {isUploading ? "Importing…" : "Import prices"}
        </button>
      </div>

      {message && <p className="mt-3 text-sm text-emerald-400">{message}</p>}
      {error && <p className="mt-3 text-sm text-rose-400">{error}</p>}
    </div>
  );
}
